import { Options, Vue } from 'vue-class-component';
import { AuthorizedUser } from '@/trust-admin-app/models/authorized-user';
import store from '@/trust-admin-app/store';
import { Inject, Watch } from 'vue-property-decorator';
import { TrustAPI } from '@/shared/modules/trust-api';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { orderBy } from 'lodash';
import AccessValidationMixin from '@/shared/mixins/access-validation';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import { EventEmitter } from 'events';
import { RouteN } from '@/trust-admin-app/constants/router-name';
import debounce from 'lodash/debounce';
import { AdvanceFilter, InvestigationTypeView, InvestigationView, Supervisor, TrustTeamMemberView, TimeEntryView, InvestigationFilterRequest } from '@/shared/modules/typescript-trustapi-client/models';
import Tag from '@/shared/components/tag/index.vue';
import { createCSV, escapeString } from '@/shared/components/helpers';
import TimesheetCalculationsMixin from '@/shared/mixins/timesheet-calculations';
import { RoleType } from '@/trust-admin-app/models/user';
import AlertBox from '@/shared/components/alert-box/index.vue';

@Options({
  components: {
    LoadingIndicator,
    ErrorIndicator,
    Tag,
    AlertBox,
  },
  mixins: [
    AccessValidationMixin,
    FormatDateTimeMixin,
    TimesheetCalculationsMixin,
  ]
})
export default class InvestigationsMain extends Vue {

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private searchKeyword = '';
  private showActiveStatus = true;
  private showTrainingStatus = false;
  private noInvestigatorAssigned = false;
  private sortMethod = 'desc';
  private activeSortHeader = 2;
  private showCauseDrivenOnly = false;
  private isReplyRequired = false;
  private cancelInvestigationAlert = false;
  private investigationIdToBeCancelled = -1;

  private tableHeadings = [
    { text: 'Practice (Trading name)', value: 'name', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Practice - branch ID', value: 'practiceBranchId', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Commencement date', value: 'commencementDate', sortMethod: 'desc', sortable: true, activeSort: false },
    { text: 'Investigation type', value: 'investigationTypeName', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Appointment status', value: 'appointmentStatus', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Reply required', value: 'replyRequired', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Status', value: 'status', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Investigator', value: 'investigator', sortMethod: 'asc', sortable: true, activeSort: false },
    { text: 'Actions', value: 'actions', sortable: false }
  ];

  private investigations: InvestigationView[] = [];
  private investigationsDisplay: any[] = [];

  private loadingContent = false;
  private errorLoadingContent = false;
  private loadingDataSet = false;
  private errorLoadingDataSet = false;
  private showAdvancedFilter = false;

  private investigators: TrustTeamMemberView[] = [];
  private investigationTypes: InvestigationTypeView[] = [];
  private supervisorList: Supervisor[] = [];

  private advanceFilter: AdvanceFilter = {
    trustTeamMemberId: null,
    supervisorId: null,
    investigationTypeId: null,
    timeFrame: 3
  }

  private filterApplied = false;
  private srchDebounced: any = null;

  @Watch('searchKeyword')
  private onKeywordChange(): void {
    setTimeout(this.srchDebounced.cancel, 0);
    setTimeout(this.srchDebounced, 0);
  }

  @Watch('showActiveStatus')
  private onShowActiveStatusChanged(): void {
    this.loadInvestigations();
  }

  @Watch('showTrainingStatus')
  private onShowTrainingStatusChanged(): void {
    this.loadInvestigations();
  }

  @Watch('noInvestigatorAssigned')
  private onNoInvestigatorAssignedChanged(): void {
    this.loadInvestigations();
  }

  @Watch('showCauseDrivenOnly')
  private onShowCauseDrivenOnlyChanged(): void {
    this.loadInvestigations();
  }

  @Watch('isReplyRequired')
  private onIsReplyRequiredChanged(): void {
    this.loadInvestigations();
  }

  created(): void {
    this.preloadData();
  }

  mounted(): void {
    window.scrollTo(0, 0);
    this.eventCtrl.on('reload-investigation-data', this.loadInvestigations);
    this.srchDebounced = debounce(() => {
      this.loadInvestigations();
    }, 1000);
  }

  beforeUnmount(): void {
    this.eventCtrl.removeListener('reload-investigation-data', this.loadInvestigations);
  }

  private async loadInvestigations(): Promise<boolean> {
    try {
      this.loadingContent = true;
      this.errorLoadingContent = false;
      const request: InvestigationFilterRequest = {
        "keyword": this.searchKeyword,
        "showActiveStatus": this.showActiveStatus,
        "showTrainingStatus": this.showTrainingStatus,
        "investigationCategoryId": this.showCauseDrivenOnly ? 2 : null,
        "isReplyRequired": this.isReplyRequired,
        "hasTrustTeamMember": !this.noInvestigatorAssigned
      };

      if (this.filterApplied) {
        request.trustTeamMemberId = this.advanceFilter.trustTeamMemberId,
          request.investigationTypeId = this.advanceFilter.investigationTypeId,
          request.timeFrame = this.advanceFilter.timeFrame,
          request.supervisorId = this.advanceFilter.supervisorId
      }

      let unassignedInvestigations: InvestigationView[] = [];
      let assignedInvestigations: InvestigationView[] = [];

      const resp: any =
        await this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationsPost(
          request
        );

      this.investigations = resp.data.data as InvestigationView[];
      this.investigations = orderBy(this.investigations, ['commencementDate'], 'desc');
      unassignedInvestigations = this.groupInvestigations(this.investigations.filter(x => !x.trustTeamMember));
      assignedInvestigations = this.groupInvestigations(this.investigations.filter(x => x.trustTeamMember));
      this.investigations = [...unassignedInvestigations, ...assignedInvestigations];

      this.investigationsDisplay = [...this.investigations];

      // convert to display table
      for (let i = 0; i < this.investigations.length; i++) {
        this.investigationsDisplay[i].name = this.investigations[i].latestPracticeLogData.branchId ?
          this.investigations[i].latestPracticeLogData.name + ' (' + this.investigations[i].latestPracticeLogData.tradingName + ')' :
          this.investigations[i].latestPracticeLogData.name;
        this.investigationsDisplay[i].practiceBranchId = this.investigations[i].latestPracticeLogData.branchId ?
          this.investigations[i].latestPracticeLogData.firmId + ' - ' + this.investigations[i].latestPracticeLogData.branchId :
          this.investigations[i].latestPracticeLogData.firmId.toString();
        this.investigationsDisplay[i].investigationTypeName = this.investigations[i].investigationType?.name + ' (' + this.investigations[i].investigationCategory?.abbreviation + ')';
        this.investigationsDisplay[i].appointmentStatus = this.investigations[i].appointments[0].appointmentStatus.name;
        this.investigationsDisplay[i].status = this.investigations[i].investigationStatus.name;
        this.investigationsDisplay[i].investigator = this.investigations[i].trustTeamMember ?
          this.investigations[i].trustTeamMember.firstName + ' ' + this.investigations[i].trustTeamMember.lastName : '';
        // this.investigationsDisplay[i].statusName = this.investigations[i].status.name;

      }

      return Promise.resolve(false);
    } catch (err) {
      this.errorLoadingContent = true;
      console.log(err);
      return Promise.reject(true);
    } finally {
      this.loadingContent = false;
    }
  }

  private groupInvestigations(investigations: InvestigationView[]): InvestigationView[] {

    let activeInvestigations: InvestigationView[] = [];
    let inactiveInvestigations: InvestigationView[] = [];
    let completedInvestigations: InvestigationView[] = [];
    let closedInvestigations: InvestigationView[] = [];
    let trainingInvestigations: InvestigationView[] = [];

    activeInvestigations = investigations.filter(x => x.investigationStatus.id == 1);
    inactiveInvestigations = investigations.filter(x => x.investigationStatus.id == 2);
    completedInvestigations = investigations.filter(x => x.investigationStatus.id == 3);
    closedInvestigations = investigations.filter(x => x.investigationStatus.id == 4);
    trainingInvestigations = investigations.filter(x => x.investigationStatus.id == 5);

    return [...activeInvestigations, ...inactiveInvestigations, ...completedInvestigations, ...closedInvestigations, ...trainingInvestigations];
  }

  private async preloadData(): Promise<void> {
    try {
      this.loadingDataSet = true;
      this.errorLoadingDataSet = false;
      const resp: any[] = await Promise.all([
        this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetTrustTeamMembersPost(),
        this.apiService.ConfigTrustApi?.apiV2ConfigTrustGetInvestigationTypesPost(),
        this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetSupervisorsGet(),
        this.loadInvestigations()
      ]);
      this.investigationTypes = resp[1].data.data as InvestigationTypeView[];
      this.investigators = orderBy(
        resp[0].data.data as TrustTeamMemberView[],
        [data => data.displayName.toLowerCase()],
        'asc'
      );
      this.supervisorList =
        orderBy(
          resp[2].data.data as Supervisor[],
          [data => data.displayName.toLowerCase()],
          'asc'
        );
    } catch (err) {
      this.errorLoadingDataSet = true;
    } finally {
      this.loadingDataSet = false;
    }
  }

  private assignInvestigator(i: InvestigationView): void {
    this.eventCtrl.emit('showAddEditInvestigationModal', i);
  }

  private applyFilter(): void {
    this.filterApplied = true;
    this.loadInvestigations();
  }

  private resetFilter(): void {
    this.filterApplied = false;
    this.advanceFilter = {
      trustTeamMemberId: null,
      supervisorId: null,
      investigationTypeId: null,
      timeFrame: 3
    };
    this.loadInvestigations();
  }


  private sortTableColumn(index: number, sortable: boolean) {
    if (sortable) {
      if (this.tableHeadings[index].sortable) {
        this.activeSortHeader = index;
        this.sortMethod = this.sortMethod === 'desc' ? 'asc' : 'desc';
        this.tableHeadings[index].sortMethod = this.sortMethod;
        if (this.investigationsDisplay.length > 0) {
          const nameSorter = index == 0 ? (c: any) => c.name.toLowerCase() : [this.tableHeadings[index].value, (c: any) => c.name.toLowerCase()];
          const orderByResults = orderBy(
            this.investigationsDisplay,
            nameSorter,
            [this.sortMethod as any]
          );
          this.investigationsDisplay = orderByResults;
        }
      }
    }
  }

  private appointmentsTippy(appointments: any[]): string {
    let _htmlString = '<div>';
    for (let i = 0; i < appointments.length; i++) {
      _htmlString += this.$formatDate(appointments[i].startDateTime) + '&nbsp;&nbsp;' + appointments[i].appointmentStatus.name + '<br>';
    }
    _htmlString += '</div>'
    return _htmlString;
  }

  private async exportDataCSV(i: InvestigationView): Promise<void> {
    const headers: any[] = [
      'Date',
      'Start time',
      'Finish time',
      'Breaks',
      'Hours',
      'Notes'
    ];

    let bodyString = '';
    let headersString = '';
    let dataString = '';
    let totalTime = 0;

    headersString = headers.map((x: any) => escapeString(x)).join(',');
    headersString += '\r\n';

    try {
      const resp: any = await this.apiService.TimeEntryApi?.apiV2TimeEntryExportTimesheetDataByInvestigationIdInvestigationIdGet(i.id);
      const practice: any = resp.data.data.investigation?.latestPracticeLogData;

      bodyString += practice.branchId == null ? practice.name : practice.name + ' (' + practice.branchTradingName + ')';
      bodyString += '\r\n';
      bodyString += practice.branchId == null ? practice.firmId : practice.firmId + ' - ' + practice.branchId;
      bodyString += '\r\n\r\n';
      bodyString += escapeString('Investigation Dates Daily Time');
      bodyString += '\r\n\r\n';
      bodyString += headersString;

      resp.data.data.timeEntries.forEach((timeEntry: TimeEntryView) => {

        dataString += [
          escapeString(this.$formatDate(timeEntry.startDateTime)),
          escapeString(this.$formatDateTimeTypeC(timeEntry.startDateTime)),
          escapeString(this.$formatDateTimeTypeC(timeEntry.endDateTime)),
          escapeString(this.$calculateHoursMins(timeEntry.breakDuration)),
          escapeString(this.$calculateHoursMins(timeEntry.workHours)),
          escapeString(timeEntry.notes),
        ].join(',');

        totalTime += timeEntry.workHours;
        dataString += '\r\n';

      });
      bodyString += dataString;
      bodyString += '\r\n\r\n';
      bodyString += escapeString('Total Time') + ',,,,';
      bodyString += totalTime;
      bodyString += '\r\n';

      createCSV(
        (practice.branchId == null ? practice.name : practice.name + '_' + practice.branchTradingName) + '_Timesheet_Data',
        bodyString
      );

    } catch (err) {
      console.log(err);
    }
  }

  private async exportCostCSV(i: InvestigationView): Promise<void> {
    const headers: any[] = [
      'Date',
      'Start time',
      'Finish time',
      'Breaks',
      'Minutes',
      'Notes',
      'Hours Worked',
      'Hours Charged',
      'KM Traveled',
      'Travel Allowance',
      'Disbursements Inc GST',
      'Disbursements Exc GST'
    ];

    let bodyString = '';
    let headersString = '';
    let dataString = '';
    let totalTime = 0;

    headersString = headers.map((x: any) => escapeString(x)).join(',');
    headersString += '\r\n';

    try {
      const resp: any = await this.apiService.TimeEntryApi?.apiV2TimeEntryExportTimesheetDataByInvestigationIdInvestigationIdGet(i.id);
      const practice: any = resp.data.data.investigation?.latestPracticeLogData;

      bodyString += practice.branchId == null ? practice.name : practice.name + ' (' + practice.branchTradingName + ')';
      bodyString += '\r\n';
      bodyString += practice.branchId == null ? practice.firmId : practice.firmId + ' - ' + practice.branchId;
      bodyString += '\r\n\r\n';
      bodyString += escapeString('Investigation Cost');
      bodyString += '\r\n\r\n';
      bodyString += headersString;

      resp.data.data.timeEntries.forEach((timeEntry: TimeEntryView) => {

        dataString += [
          escapeString(this.$formatDate(timeEntry.startDateTime)),
          escapeString(this.$formatDateTimeTypeC(timeEntry.startDateTime)),
          escapeString(this.$formatDateTimeTypeC(timeEntry.endDateTime)),
          escapeString(this.$calculateHoursMins(timeEntry.breakDuration)),
          timeEntry.workHours,
          escapeString(timeEntry.notes),
          escapeString(this.$calculateHoursMins(timeEntry.workHours)),
        ].join(',');

        totalTime += timeEntry.workHours;
        dataString += '\r\n';

      });
      bodyString += dataString;
      bodyString += '\r\n\r\n';
      bodyString += escapeString('Total Time') + ',,,,';
      bodyString += totalTime + ',,' + escapeString(this.$calculateHoursMins(totalTime));
      bodyString += '\r\n';

      createCSV(
        (practice.branchId == null ? practice.name : practice.name + '_' + practice.branchTradingName) + 'Timesheet_Cost',
        bodyString
      );

    } catch (err) {
      console.log(err);
    }
  }

  get isSuperAdmin(): boolean {
    return this.$accessPermitted(this.usrAcct.user.roles, [
      RoleType.TrustSuperAdmin
    ]);
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  get usrAcct(): AuthorizedUser {
    return store.getters.getUserAccount;
  }

  get RouteN(): typeof RouteN {
    return RouteN;
  }

  private cancelInvestigation(id: number) {
    this.investigationIdToBeCancelled = id;
    this.cancelInvestigationAlert = true;
  }

  private continueClicked() {
    this.apiService.InvestigationApi?.apiV2InvestigationCancelInvestigationPost({ id: this.investigationIdToBeCancelled })
      .then(res => {
        this.loadInvestigations();
        this.cancelInvestigationAlert = false;
      })
      .catch(err => {
        this.cancelInvestigationAlert = false;
      })
  }

}

